import { Button, codeNormalStyles, icons, labelDefaultMediumStyles, labelDefaultRegularStyles } from '@lifebit-ai/lds'
import styled from 'styled-components'

import { CardHeading } from 'ui/card/card.styles'

export { ItemIcon, ItemInfo, ItemRow, ItemSubtitle, ItemTitle } from './components/FileDetails/FileDetails.styles'

export const RowActionButton = styled(Button)`
  &:hover {
    background: ${({ theme }) => theme.color.background.highlight};
  }

  svg {
    width: ${({ theme }) => theme.size.icon.small};
    height: ${({ theme }) => theme.size.icon.small};

    path {
      fill: ${({ theme }) => theme.color.content.main};
    }
  }
`

export const CardContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.size.space['04']};
`

/** The export page's card titles are the same heading the request rail cards use. */
export const CardSectionTitle = CardHeading

export const MetadataSection = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  gap: ${({ theme }) => theme.size.space['04']};
`

export const MetadataRow = styled.div`
  align-items: flex-start;
  display: grid;
  gap: ${({ theme }) => theme.size.space['04']};
  grid-template-columns: 260px 1fr;
`

export const MetadataLabel = styled.span<{ $alignSelf?: 'center' | 'flex-start' }>`
  ${labelDefaultRegularStyles}
  align-items: center;
  align-self: ${({ $alignSelf }) => $alignSelf ?? 'center'};
  color: ${({ theme }) => theme.color.content.secondary};
  display: inline-flex;
  /* Enough air for the info icon to read as its own affordance beside the label. */
  gap: ${({ theme }) => theme.size.space['02']};
`

export const MetadataInfoTrigger = styled.span`
  cursor: pointer;
  display: inline-flex;
`

export const MetadataInfoIcon = styled(icons.InfoSmall)`
  flex-shrink: 0;
  height: ${({ theme }) => theme.size.icon.small};
  width: ${({ theme }) => theme.size.icon.small};

  path {
    fill: ${({ theme }) => theme.color.content.secondary};
  }
`

export const MetadataValue = styled.span`
  ${labelDefaultMediumStyles}
  color: ${({ theme }) => theme.color.content.main};
  min-width: 0;
  word-break: break-word;
`

/** Absence of a value, e.g. "No restriction was applied": quieter than a real value. */
export const MetadataEmptyValue = styled.span`
  ${labelDefaultRegularStyles}
  color: ${({ theme }) => theme.color.content.subtle as string};
  min-width: 0;
  word-break: break-word;
`

/** The SQL row: the query itself, with an eye that opens the full statement in a modal. */
export const SqlQueryValue = styled.span`
  align-items: center;
  display: inline-flex;
  gap: ${({ theme }) => theme.size.space['02']};
  min-width: 0;
`

/** A query can run to many lines, so the row shows one and lets the modal show the rest. */
export const SqlQueryText = styled.span`
  ${labelDefaultMediumStyles}
  color: ${({ theme }) => theme.color.content.main};
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`

export const SqlQueryTrigger = styled.button`
  align-items: center;
  appearance: none;
  background: none;
  border: none;
  cursor: pointer;
  display: inline-flex;
  flex-shrink: 0;
  padding: 0;

  svg {
    height: ${({ theme }) => theme.size.icon.small};
    width: ${({ theme }) => theme.size.icon.small};

    path {
      fill: ${({ theme }) => theme.color.content.secondary};
    }
  }

  &:hover svg path {
    fill: ${({ theme }) => theme.color.content.main};
  }
`

/** SQL shown in the modal: monospace, scrollable, wraps rather than overflowing. */
export const SqlQueryBlock = styled.pre`
  ${codeNormalStyles}
  background: ${({ theme }) => theme.color.background.highlight as string};
  border-radius: ${({ theme }) => theme.size.border.radius['02']};
  color: ${({ theme }) => theme.color.content.main as string};
  margin: 0;
  max-height: 60vh;
  overflow: auto;
  padding: ${({ theme }) => theme.size.space['04']};
  white-space: pre-wrap;
  word-break: break-word;
`

/** Hugs the dataset name: as a block it stretched its tint across the whole value column. */
export const DatasetValue = styled.div`
  align-items: center;
  display: inline-flex;
  flex-wrap: wrap;
  width: fit-content;
  gap: ${({ theme }) => theme.size.space['03']};
  min-width: 0;
  background: ${({ theme }) => theme.color.background.main};
  padding: ${({ theme }) => theme.size.space['03']};
  border-radius: ${({ theme }) => theme.size.border.radius['025']};
`

export const DatasetColorIndicator = styled.span<{ $color: string }>`
  background: ${({ $color }) => $color};
  border-radius: ${({ theme }) => theme.size.border.radius['02']};
  flex-shrink: 0;
  height: ${({ theme }) => theme.size.icon.small};
  width: ${({ theme }) => theme.size.icon.small};
`

export const SharingStatusValue = styled.span`
  align-items: center;
  display: inline-flex;
  gap: ${({ theme }) => theme.size.space['02']};
  min-width: 0;
`

/** Mirrors web-application's `DatasetParticipantCount` so cohort sizes read the same way. */
export const ParticipantCount = styled.span`
  align-items: center;
  /* The count reads as a value like any other; only the icon stays quiet. */
  color: ${({ theme }) => theme.color.content.main};
  display: inline-flex;
  gap: ${({ theme }) => theme.size.space['01']};

  svg {
    height: ${({ theme }) => theme.size.icon.small};
    width: ${({ theme }) => theme.size.icon.small};

    path {
      fill: ${({ theme }) => theme.color.content.secondary};
    }
  }
`

export const MetadataSubsection = styled.div<{ $separated?: boolean }>`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.size.space['03']};
  margin-top: ${({ $separated, theme }) => ($separated ? theme.size.space['05'] : 0)};
`

export const MetadataSubsectionTitle = styled.span`
  ${labelDefaultMediumStyles}
  color: ${({ theme }) => theme.color.content.main};
`
